import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { ProjectStatusBadge } from './ProjectStatusBadge';

// Project interface for cost report projects
interface CostReportProject {
  'Nombre Proyecto': string;
  'Codigo Centro Costos': number | null;
  'Tipo Centro Costos': string | null;
  'Ultima Actualizacion Sistema': string | null;
  'Ultima Actualizacion Usuario': string | null;
  active: boolean;
}

interface CostReportProjectSelectorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  showActiveOnly?: boolean;
  showStatusBadges?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export const CostReportProjectSelector: React.FC<CostReportProjectSelectorProps> = ({
  value,
  onChange,
  label = 'Proyecto',
  placeholder = 'Seleccionar proyecto...',
  disabled = false,
  fullWidth = true,
  size = 'medium',
  showActiveOnly = false,
  showStatusBadges = true,
  required = false,
  error = false,
  helperText
}) => {
  const [projects, setProjects] = useState<CostReportProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, [showActiveOnly]);

  const loadProjects = async () => {
    setLoading(true);
    setLoadError(null);
    
    try {
      // Use the new API endpoint that gets projects with cost data and status
      const response = await fetch('/api/proyectos-costos');
      
      if (!response.ok) {
        throw new Error('Error al cargar proyectos');
      }
      
      const projectsData: CostReportProject[] = await response.json();
      
      // Filter by active status if requested
      let filteredProjects = projectsData;
      if (showActiveOnly) {
        filteredProjects = projectsData.filter(project => project.active);
      }
      
      setProjects(filteredProjects);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setLoadError(errorMessage);
      console.error('Error loading cost report projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const renderProjectOption = (project: CostReportProject) => {
    const projectName = project['Nombre Proyecto'];
    const isActive = project.active;
    
    return (
      <Box
        key={projectName}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
          // Apply gray styling for inactive projects as per requirements
          opacity: isActive ? 1 : 0.7,
          color: isActive ? 'inherit' : 'text.secondary'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: isActive ? 'normal' : 400,
            color: isActive ? 'inherit' : 'text.secondary'
          }}
        >
          {projectName}
        </Typography>
        {showStatusBadges && (
          <ProjectStatusBadge
            project={{
              'Codigo Centro Costos': project['Codigo Centro Costos'] || 0,
              'Nombre Proyecto': project['Nombre Proyecto'],
              'Tipo Centro Costos': project['Tipo Centro Costos'] || '',
              'Ultima Actualizacion Sistema': project['Ultima Actualizacion Sistema'],
              'Ultima Actualizacion Usuario': project['Ultima Actualizacion Usuario'],
              active: project.active
            }}
            size="small"
            showText={false}
            showIcon={true}
          />
        )}
      </Box>
    );
  };

  if (loadError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {loadError}
      </Alert>
    );
  }

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || loading}
      required={required}
      error={error}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={handleChange}
        label={label}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography variant="body2" color="text.secondary">
                {placeholder}
              </Typography>
            );
          }
          
          // Find the selected project to show its status
          const selectedProject = projects.find(p => p['Nombre Proyecto'] === selected);
          if (!selectedProject) {
            return selected;
          }
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: selectedProject.active ? 'inherit' : 'text.secondary',
                  fontWeight: selectedProject.active ? 'normal' : 400
                }}
              >
                {selected}
              </Typography>
              {showStatusBadges && (
                <ProjectStatusBadge
                  project={{
                    'Codigo Centro Costos': selectedProject['Codigo Centro Costos'] || 0,
                    'Nombre Proyecto': selectedProject['Nombre Proyecto'],
                    'Tipo Centro Costos': selectedProject['Tipo Centro Costos'] || '',
                    'Ultima Actualizacion Sistema': selectedProject['Ultima Actualizacion Sistema'],
                    'Ultima Actualizacion Usuario': selectedProject['Ultima Actualizacion Usuario'],
                    active: selectedProject.active
                  }}
                  size="small"
                  showText={false}
                  showIcon={true}
                />
              )}
            </Box>
          );
        }}
      >
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">
            {placeholder}
          </Typography>
        </MenuItem>
        {loading ? (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Cargando proyectos...</Typography>
            </Box>
          </MenuItem>
        ) : (
          projects.map((project) => (
            <MenuItem
              key={project['Nombre Proyecto']}
              value={project['Nombre Proyecto']}
              sx={{
                // Ensure inactive projects remain selectable but visually distinct
                '&:hover': {
                  backgroundColor: project.active ? 'action.hover' : 'action.selected'
                }
              }}
            >
              {renderProjectOption(project)}
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default CostReportProjectSelector;